import type {UserShortcut} from '@github-ui/saved-views'
import {useCallback} from 'react'

import {SavedViewIssueList} from '../../components/views/SavedViewIssueList'
import type {SavedView} from '../../components/views/types'
import {PreviewQueryProvider} from '../../contexts/PreviewQueryContext'
import {useConfirmDiscardChanges} from '../../hooks/use-confirm-discard-changes'
import {useSavedViewEditor} from '../../hooks/use-saved-view-editor'
import styles from './RepositorySavedViewPage.module.css'
import {SavedViewEditHeader} from './SavedViewEditHeader'

function toShortcut(view: SavedView): UserShortcut {
  return {
    id: String(view.id),
    name: view.name,
    searchType: 'issues',
    query: view.query,
    description: view.description ?? '',
    icon: view.icon,
    color: view.color,
  }
}

interface SavedViewEditViewProps {
  savedView: SavedView
  owner: string
  repo: string
  onSaved: () => void
  onCancel: () => void
}

export function SavedViewEditView({savedView, owner, repo, onSaved, onCancel}: SavedViewEditViewProps) {
  const {shortcut, setShortcutValue, previewQuery, commitPreviewQuery, previewQueryContextValue, isDirty} =
    useSavedViewEditor({
      initialShortcut: () => toShortcut(savedView),
    })

  const {confirmDiscardIfDirty} = useConfirmDiscardChanges({isDirty})

  const handleCancel = useCallback(async () => {
    if (await confirmDiscardIfDirty()) {
      onCancel()
    }
  }, [confirmDiscardIfDirty, onCancel])

  return (
    <>
      <SavedViewEditHeader
        savedView={savedView}
        owner={owner}
        repo={repo}
        shortcut={shortcut}
        setShortcutValue={setShortcutValue}
        onPreviewQuerySubmit={commitPreviewQuery}
        onSaved={onSaved}
        onCancel={handleCancel}
      />
      <div className={styles.middlePaneGrid}>
        <PreviewQueryProvider value={previewQueryContextValue}>
          <SavedViewIssueList owner={owner} repo={repo} query={previewQuery} preheatEnabled={false} />
        </PreviewQueryProvider>
      </div>
    </>
  )
}
